<template>
  <div class="aa-page">
    <div class="aa-header">
      <div>
        <h2 class="aa-title"><feather-icon icon="UsersIcon" size="22" class="mr-75" />Afiliados</h2>
        <p class="aa-sub">Gerencie candidaturas ao programa de afiliados</p>
      </div>
      <a href="/afiliados" target="_blank" class="aa-btn aa-btn--ghost">
        <feather-icon icon="ExternalLinkIcon" size="14" class="mr-50" />Ver pagina publica
      </a>
    </div>

    <!-- Stats -->
    <div v-if="stats" class="aa-stats">
      <div class="aa-stat">
        <div class="aa-stat__value">{{ stats.total || 0 }}</div>
        <div class="aa-stat__label">Total</div>
      </div>
      <div class="aa-stat aa-stat--warn">
        <div class="aa-stat__value">{{ stats.pending || 0 }}</div>
        <div class="aa-stat__label">Pendentes</div>
      </div>
      <div class="aa-stat aa-stat--ok">
        <div class="aa-stat__value">{{ stats.approved || 0 }}</div>
        <div class="aa-stat__label">Aprovados</div>
      </div>
      <div class="aa-stat aa-stat--danger">
        <div class="aa-stat__value">{{ stats.rejected || 0 }}</div>
        <div class="aa-stat__label">Rejeitados</div>
      </div>
      <div class="aa-stat">
        <div class="aa-stat__value">{{ stats.last_7d || 0 }}</div>
        <div class="aa-stat__label">Ultimos 7 dias</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="aa-filters">
      <button class="aa-filter" :class="{ 'aa-filter--active': statusFilter === '' }" @click="statusFilter = ''; fetch()">Todos</button>
      <button class="aa-filter" :class="{ 'aa-filter--active': statusFilter === 'pending' }" @click="statusFilter = 'pending'; fetch()">Pendentes</button>
      <button class="aa-filter" :class="{ 'aa-filter--active': statusFilter === 'approved' }" @click="statusFilter = 'approved'; fetch()">Aprovados</button>
      <button class="aa-filter" :class="{ 'aa-filter--active': statusFilter === 'rejected' }" @click="statusFilter = 'rejected'; fetch()">Rejeitados</button>
    </div>

    <div v-if="loading" class="aa-loading">Carregando...</div>

    <div v-else-if="!items.length" class="aa-empty">
      <feather-icon icon="InboxIcon" size="40" />
      <p>Nenhum afiliado encontrado</p>
    </div>

    <div v-else class="aa-list">
      <div v-for="item in items" :key="item.id" class="aa-card" :class="'aa-card--' + item.status">
        <div class="aa-card__header">
          <div class="aa-card__avatar">{{ (item.name || '?')[0].toUpperCase() }}</div>
          <div class="aa-card__info">
            <div class="aa-card__name">{{ item.name }}</div>
            <div class="aa-card__email">{{ item.email }}</div>
          </div>
          <span class="aa-badge" :class="'aa-badge--' + item.status">
            {{ item.status === 'approved' ? 'Aprovado' : item.status === 'rejected' ? 'Rejeitado' : 'Pendente' }}
          </span>
        </div>

        <div class="aa-card__details">
          <div class="aa-detail" v-if="item.phone">
            <feather-icon icon="PhoneIcon" size="13" class="mr-50" />
            <a :href="'https://wa.me/' + item.phone.replace(/\D/g,'')" target="_blank">{{ item.phone }}</a>
          </div>
          <div class="aa-detail" v-if="item.social">
            <feather-icon icon="GlobeIcon" size="13" class="mr-50" />
            <a v-if="item.social.startsWith('http')" :href="item.social" target="_blank">{{ item.social }}</a>
            <span v-else>{{ item.social }}</span>
          </div>
          <div class="aa-detail" v-if="item.niche">
            <feather-icon icon="TagIcon" size="13" class="mr-50" />
            {{ nicheLabel(item.niche) }}
          </div>
          <div class="aa-detail" v-if="item.audience_size">
            <feather-icon icon="TrendingUpIcon" size="13" class="mr-50" />
            {{ item.audience_size }} seguidores
          </div>
          <div class="aa-detail">
            <feather-icon icon="CalendarIcon" size="13" class="mr-50" />
            {{ formatDate(item.created_at) }}
          </div>
          <div class="aa-detail" v-if="item.utm_source">
            <feather-icon icon="Share2Icon" size="13" class="mr-50" />
            {{ [item.utm_source, item.utm_medium, item.utm_campaign].filter(Boolean).join(' / ') }}
          </div>
        </div>

        <div v-if="item.motivation" class="aa-card__motivation">
          <strong>Motivacao:</strong> {{ item.motivation }}
        </div>

        <!-- Notes -->
        <div class="aa-card__notes">
          <textarea
            v-model="item._notes"
            class="aa-notes-input"
            placeholder="Notas internas sobre este afiliado..."
            rows="2"
            @blur="saveNotes(item)"
          ></textarea>
        </div>

        <!-- Actions -->
        <div class="aa-card__actions">
          <button v-if="item.status !== 'approved'" class="aa-action aa-action--approve" @click="updateStatus(item, 'approved')">
            <feather-icon icon="CheckIcon" size="14" class="mr-25" />Aprovar
          </button>
          <button v-if="item.status !== 'rejected'" class="aa-action aa-action--reject" @click="updateStatus(item, 'rejected')">
            <feather-icon icon="XIcon" size="14" class="mr-25" />Rejeitar
          </button>
          <button v-if="item.status !== 'pending'" class="aa-action" @click="updateStatus(item, 'pending')">
            <feather-icon icon="ClockIcon" size="14" class="mr-25" />Pendente
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AfiliadosAdmin',
  data() {
    return { items: [], stats: null, loading: true, statusFilter: '' }
  },
  mounted() { this.fetch() },
  methods: {
    api(method, url, body) {
      const opts = { baseURL: '' }
      if (method === 'get') return this.$http.get(url, opts)
      return this.$http[method](url, body, opts)
    },
    async fetch() {
      this.loading = true
      try {
        let url = '/api/dkbackend/admin/affiliate-signups'
        if (this.statusFilter) url += `?status=${this.statusFilter}`
        const { data } = await this.api('get', url)
        this.items = (data.data || []).map(i => ({ ...i, _notes: i.notes || '' }))
        this.stats = data.stats || {}
      } catch (e) { console.error(e) }
      this.loading = false
    },
    async updateStatus(item, status) {
      try {
        await this.api('put', `/api/dkbackend/admin/affiliate-signups/${item.id}`, { status })
        item.status = status
        this.$message({ type: 'success', message: status === 'approved' ? 'Afiliado aprovado!' : status === 'rejected' ? 'Afiliado rejeitado.' : 'Status atualizado.' })
        this.fetch()
      } catch {
        this.$message({ type: 'error', message: 'Erro ao atualizar' })
      }
    },
    async saveNotes(item) {
      if (item._notes === (item.notes || '')) return
      try {
        await this.api('put', `/api/dkbackend/admin/affiliate-signups/${item.id}`, { notes: item._notes })
        item.notes = item._notes
      } catch {}
    },
    nicheLabel(niche) {
      const map = { infoprodutos: 'Infoprodutos / Cursos', afiliado: 'Afiliado Digital', agencia: 'Agencia / Trafego', social_media: 'Social Media', consultoria: 'Consultoria', ecommerce: 'E-commerce', outro: 'Outro' }
      return map[niche] || niche
    },
    formatDate(d) {
      if (!d) return '-'
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
  },
}
</script>

<style lang="scss" scoped>
$orange: #E86A10;

.aa-page { padding: 0; }
.aa-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
.aa-title { display: flex; align-items: center; font-size: 22px; font-weight: 800; color: #0f172a; margin: 0; }
.aa-sub { font-size: 13.5px; color: #64748b; margin: 4px 0 0; }
.aa-btn {
  padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 700;
  cursor: pointer; border: none; font-family: inherit; display: inline-flex; align-items: center; text-decoration: none;
  &--ghost { background: #fff; border: 1.5px solid #e2e8f0; color: #475569; &:hover { border-color: $orange; color: $orange; } }
}

/* Stats */
.aa-stats { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
.aa-stat {
  flex: 1; min-width: 120px; background: #fff; border: 1px solid #e2e8f0;
  border-radius: 12px; padding: 16px; text-align: center;
}
.aa-stat__value { font-size: 24px; font-weight: 900; color: #0f172a; }
.aa-stat__label { font-size: 12px; color: #64748b; margin-top: 2px; }
.aa-stat--warn .aa-stat__value { color: #D97706; }
.aa-stat--ok .aa-stat__value { color: #059669; }
.aa-stat--danger .aa-stat__value { color: #DC2626; }

/* Filters */
.aa-filters { display: flex; gap: 6px; margin-bottom: 20px; }
.aa-filter {
  padding: 7px 16px; border-radius: 8px; border: 1.5px solid #e2e8f0;
  background: #fff; font-size: 13px; font-weight: 600; color: #64748b;
  cursor: pointer; font-family: inherit;
  &--active { background: $orange; border-color: $orange; color: #fff; }
}

.aa-loading, .aa-empty { text-align: center; padding: 60px; color: #94a3b8; }

/* Card list */
.aa-list { display: flex; flex-direction: column; gap: 16px; }
.aa-card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 20px;
  border-left: 4px solid #94a3b8;
  &--pending { border-left-color: #D97706; }
  &--approved { border-left-color: #059669; }
  &--rejected { border-left-color: #DC2626; }
}

.aa-card__header { display: flex; align-items: center; gap: 14px; margin-bottom: 14px; }
.aa-card__avatar {
  width: 42px; height: 42px; border-radius: 50%;
  background: linear-gradient(135deg, $orange, #F59E0B);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 800; font-size: 17px; flex-shrink: 0;
}
.aa-card__info { flex: 1; min-width: 0; }
.aa-card__name { font-size: 15px; font-weight: 800; color: #0f172a; }
.aa-card__email { font-size: 13px; color: #64748b; }

.aa-badge {
  padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; flex-shrink: 0;
  &--pending { background: #FEF3C7; color: #92400E; }
  &--approved { background: #D1FAE5; color: #065F46; }
  &--rejected { background: #FEE2E2; color: #991B1B; }
}

.aa-card__details { display: flex; flex-wrap: wrap; gap: 12px 24px; margin-bottom: 12px; }
.aa-detail {
  display: flex; align-items: center; font-size: 13px; color: #475569;
  a { color: $orange; text-decoration: none; &:hover { text-decoration: underline; } }
}

.aa-card__motivation {
  font-size: 13px; color: #475569; line-height: 1.6; margin-bottom: 12px;
  padding: 10px 14px; background: #f8fafc; border-radius: 8px;
  strong { color: #334155; }
}

.aa-card__notes { margin-bottom: 12px; }
.aa-notes-input {
  width: 100%; padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 8px;
  font-size: 13px; font-family: inherit; color: #334155; resize: vertical; outline: none;
  &:focus { border-color: $orange; }
}

.aa-card__actions { display: flex; gap: 8px; }
.aa-action {
  display: flex; align-items: center; padding: 7px 14px; border-radius: 8px;
  border: 1px solid #e2e8f0; background: #fff; font-size: 12px; font-weight: 700;
  color: #475569; cursor: pointer; font-family: inherit;
  &:hover { border-color: #94a3b8; }
  &--approve { color: #059669; border-color: #D1FAE5; background: #F0FDF4; &:hover { background: #D1FAE5; } }
  &--reject { color: #DC2626; border-color: #FEE2E2; background: #FEF2F2; &:hover { background: #FEE2E2; } }
}
</style>
